import { observer } from "mobx-react";
import {
  GeneralMeetingElectronicParticipation,
  GeneralMeetingForm,
  GeneralMeetingVoting,
  generalMeetingVotingApprovedLabels,
  GeneralMeetingVotingType,
  generalMeetingVotingTypeDescriptions,
  generalMeetingVotingTypeLabels,
  generalMeetingVotingTypeSelectedLabels,
  VotingModeOption,
  votingOptionsLabels,
} from "./commonGeneralMeetingDomain";
import { LocalDateTimePicker } from "./commonComponents";
import { Alert } from "../../components-v2/Alert";
import { Box, VBox } from "../../components-v2/Box";
import { MTextInput } from "../../components-v2/TextInput";
import { GeneralMeetingVotingModel } from "../generalMeetingMinutes/generalMeetingMinutesDomain";
import { FC } from "react";
import { LiveVotingResults } from "../../meetings/generalMeeting/electronicGeneralMeetingDomain";
import { percentOf } from "../../utils/numbers";
import { RadioButtonGroup } from "../../components-v2/RadioButton";
import { identity } from "lodash";
import { useLocaleFormat, useTranslation } from "../../stores/RootStoreContext";
import { MeetingPlace } from "../../meetings/meetingsDomain";
import {
  LocalDateFieldModel,
  RadioFieldModel,
  StringFieldModel,
  TimeFieldModel,
} from "../../common/commonForms";
import { RadioFieldQuestion } from "../../common/commonComponents";
import { InfoButton } from "../../components-v2/InfoButton";
import { Button, IconButton } from "../../components-v2/Button";
import { Dialog } from "../../components-v2/Dialog";
import { Icon } from "../../components-v2/Icon";

export const TimeOfGeneralMeeting = observer(function ({
  dateField,
  timeField,
  isLocked,
}: {
  dateField: LocalDateFieldModel;
  timeField: TimeFieldModel;
  isLocked?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <VBox>
      <div>
        {t("documents.generalMeeting.dateQuestion")}
        &nbsp;
        <InfoButton infoText={t("documents.generalMeeting.date-guidancenote")} />
      </div>
      <LocalDateTimePicker
        dateLabel={{ key: "documents.generalMeeting.date" }}
        dateField={dateField}
        timeLabel={{ key: "documents.generalMeeting.time" }}
        timeField={timeField}
        isLocked={isLocked}
      />
    </VBox>
  );
});

export const PlaceOfGeneralMeeting = observer(function PlaceOfGeneralMeeting({
  meetingForm,
  meetingPlace,
  inlineAddressValue,
  isLocked,
}: {
  meetingForm: RadioFieldModel<GeneralMeetingForm>;
  meetingPlace: RadioFieldModel<MeetingPlace>;
  inlineAddressValue: StringFieldModel;
  isLocked?: boolean;
}) {
  const { t } = useTranslation();

  if (meetingForm.value !== GeneralMeetingForm.physical_meeting) {
    return null;
  }

  return (
    <>
      <RadioFieldQuestion
        question={{ key: "documents.generalMeeting.place" }}
        field={meetingPlace}
        fieldName={"meetingPlace"}
        infoText={{ key: "documents.generalMeeting.place-guidancenote" }}
        row
        isLocked={isLocked}
      />
      {meetingPlace.value === MeetingPlace.inlineAddress && (
        <MTextInput
          label={t("documents.meetingForm.address")}
          model={inlineAddressValue}
          readOnly={isLocked}
        />
      )}
    </>
  );
});

export const ElectronicParticipationView = observer(function ElectronicParticipation({
  meetingForm,
  electronicParticipation,
  electronicParticipationLink,
  isLocked,
}: {
  meetingForm: RadioFieldModel<GeneralMeetingForm>;
  electronicParticipation: RadioFieldModel<GeneralMeetingElectronicParticipation>;
  electronicParticipationLink?: StringFieldModel;
  isLocked?: boolean;
}) {
  const { t } = useTranslation();

  return (
    <>
      <RadioFieldQuestion
        question={{
          key:
            meetingForm.value === GeneralMeetingForm.physical_meeting
              ? "documents.meetingForm.electronicParticipationAtPhysicalMeetingQuestion"
              : "documents.meetingForm.electronicParticipationQuestion",
        }}
        field={electronicParticipation}
        fieldName={"electronicParticipation"}
        isLocked={isLocked}
      />

      {electronicParticipation.value ===
        GeneralMeetingElectronicParticipation.ELECTRONIC_GM_NOT_OFFERED && (
        <Alert
          type="warning"
          message={t("documents.meetingForm.electronicParticipationNotOffered.warningText")}
        />
      )}

      {electronicParticipation.value === GeneralMeetingElectronicParticipation.LINK_GIVEN &&
        electronicParticipationLink && (
          <MTextInput
            label={t("documents.meetingForm.electronicParticipationLink")}
            name={"electronicParticipationLink"}
            model={electronicParticipationLink}
            readOnly={isLocked}
          />
        )}
    </>
  );
});

export const VotingTypeSettings = observer(function VotingTypeSettings({
  defaultVotingType,
  isApproved,
  isDisabled,
  isVotesRegistered,
  voting,
  onSaveVotingType,
}: {
  defaultVotingType: GeneralMeetingVotingType;
  isApproved?: boolean;
  isDisabled: boolean;
  isVotesRegistered: boolean;
  voting: GeneralMeetingVotingModel;
  onSaveVotingType?: (votingType?: GeneralMeetingVotingType) => void;
}) {
  const { t } = useTranslation();
  return (
    <Box alignItems={"center"}>
      {isVotesRegistered && (
        <Box>
          {isApproved && <Icon name={"check"} color={"info"} />}
          {t(
            generalMeetingVotingApprovedLabels(
              generalMeetingVotingTypeLabels[voting.selectedVotingType(defaultVotingType)],
              isApproved,
            ),
          )}
        </Box>
      )}
      {!isVotesRegistered &&
        t(generalMeetingVotingTypeSelectedLabels[voting.selectedVotingType(defaultVotingType)])}
      {!isDisabled && (
        <>
          <IconButton
            icon={"gear"}
            tooltip={{ key: "general-meeting.voting-type.title" }}
            onClick={() => voting.openVotingTypeSettingsDialog(defaultVotingType)}
          />
          {voting.newSelectedVotingType && (
            <VotingTypeSettingsDialog
              defaultVotingType={defaultVotingType}
              model={voting}
              onSave={onSaveVotingType}
            />
          )}
        </>
      )}
    </Box>
  );
});

export const VotingTypeSettingsDialog: FC<{
  defaultVotingType: GeneralMeetingVotingType;
  model: GeneralMeetingVotingModel;
  onSave?: (votingType?: GeneralMeetingVotingType) => void;
}> = observer(({ defaultVotingType, model, onSave }) => {
  return (
    <Dialog
      title={{ key: "general-meeting.voting-type.title" }}
      submitLabel={{ key: "save" }}
      onSubmit={() => {
        model.saveVotingType();
        onSave?.(model.votingType);
      }}
      onCancel={() => model.closeVotingTypeSettingsDialog()}
    >
      <VBox gap={"lg"}>
        <RadioButtonGroup<GeneralMeetingVotingType>
          idFn={identity}
          labelFn={(v) =>
            v === defaultVotingType
              ? {
                  key: "general-meeting.voting-type.assumed",
                  params: { votingTypeLabel: generalMeetingVotingTypeLabels[v] },
                }
              : generalMeetingVotingTypeLabels[v]
          }
          descriptionFn={(v) => generalMeetingVotingTypeDescriptions[v]}
          options={Object.values(GeneralMeetingVotingType)}
          selected={model.newSelectedVotingType}
          onChange={(_, selected) => {
            model.setNewSelectedVotingType(selected);
          }}
        />
        {defaultVotingType !== model.newSelectedVotingType && (
          <>
            {defaultVotingType === GeneralMeetingVotingType.SIMPLE_MAJORITY && (
              <Alert
                message={{ key: "general-meeting.voting-type.simple.warning" }}
                type={"warning"}
              />
            )}
            {defaultVotingType === GeneralMeetingVotingType.QUALIFIED && (
              <Alert
                message={{ key: "general-meeting.voting-type.qualified.warning" }}
                type={"warning"}
              />
            )}
          </>
        )}
      </VBox>
    </Dialog>
  );
});

export const VotingOutcomeSettings: FC<{
  model: GeneralMeetingVotingModel;
  data?: LiveVotingResults;
  isDisabled: boolean;
  isElectronicGeneralMeeting?: boolean;
  onChange: (voting: GeneralMeetingVoting) => void;
  onLinkClick?: () => void;
}> = observer(
  ({ model, data, isDisabled, isElectronicGeneralMeeting = true, onChange, onLinkClick }) => {
    const { t } = useTranslation();
    const { formatPercent } = useLocaleFormat();

    return (
      <VBox marginTop="lg">
        <Box justifyContent={"space-between"}>
          <h5>{t("general-meeting.minutes.voting")}</h5>
        </Box>
        <RadioButtonGroup<VotingModeOption>
          idFn={identity}
          labelFn={(v) => votingOptionsLabels[v]}
          descriptionFn={(v) => {
            if (v === VotingModeOption.LIVE_VOTING && model.liveVoting !== undefined) {
              return (
                <VBox>
                  <p>
                    {t("n-voted-for", { n: { type: "number", value: model.liveVoting?.votesFor } })}
                  </p>
                  <p>
                    {t("n-voted-against", {
                      n: { type: "number", value: model.liveVoting?.votesAgainst },
                    })}
                  </p>
                  <p>
                    {t("n-voted-blank", {
                      n: {
                        type: "number",
                        value:
                          model.representedVotes -
                          model.liveVoting?.votesFor -
                          model.liveVoting?.votesAgainst,
                      },
                    })}
                  </p>
                  <p className={"bold"}>
                    {t("n-voted-for", {
                      n: formatPercent(
                        percentOf(model.liveVoting.votesFor, model.numberOfLiveVotesCast),
                      ),
                    })}
                  </p>
                </VBox>
              );
            } else if (v === VotingModeOption.MANUAL && model.selectedVotingMode === v) {
              return (
                <VBox paddingY={"md"}>
                  <Box>
                    <MTextInput
                      model={model.votesFor}
                      onBlur={() => {
                        model.votesFor.commit();
                        onChange(model.toJSON());
                      }}
                      label={t("decisions.votes.votesFor")}
                      disabled={isDisabled}
                    />
                    <MTextInput
                      model={model.votesAgainst}
                      onBlur={() => {
                        model.votesAgainst.commit();
                        onChange(model.toJSON());
                      }}
                      label={t("decisions.votes.votesAgainst")}
                      disabled={isDisabled}
                    />
                    <MTextInput
                      model={model.votesBlank}
                      onBlur={() => {
                        model.votesBlank.commit();
                        onChange(model.toJSON());
                      }}
                      label={t("decisions.votes.votesBlank")}
                      disabled={isDisabled}
                    />
                  </Box>
                  <Box>
                    <MTextInput
                      model={model.shareCapitalFor}
                      onBlur={() => onChange(model.toJSON())}
                      label={t("decisions.votes.shareCapitalFor")}
                      disabled={isDisabled}
                    />
                    <MTextInput
                      model={model.shareCapitalAgainst}
                      onBlur={() => onChange(model.toJSON())}
                      label={t("decisions.votes.shareCapitalAgainst")}
                      disabled={isDisabled}
                    />
                    <MTextInput
                      model={model.shareCapitalBlank}
                      onBlur={() => onChange(model.toJSON())}
                      label={t("decisions.votes.shareCapitalBlank")}
                      disabled={isDisabled}
                    />
                  </Box>
                </VBox>
              );
            }
          }}
          options={Object.values(VotingModeOption)}
          disabledFn={(item) =>
            (item === VotingModeOption.LIVE_VOTING &&
              model.liveVoting === undefined &&
              data === undefined) ||
            (item === VotingModeOption.INDIVIDUAL &&
              (isElectronicGeneralMeeting || onLinkClick === undefined))
          }
          selected={model.selectedVotingMode}
          onChange={(_, selected) => {
            model.setSelectedVotingMode(selected, data);
            onChange(model.toJSON());
          }}
          disabled={isDisabled}
        />
        {model.selectedVotingMode === VotingModeOption.INDIVIDUAL && (
          <VBox marginLeft={"lg"} paddingY={"md"} gap={"md"} className={"subtle"} textSize={"sm"}>
            {model.individualVoting && model.isIndividualVotesRegistered && (
              <VBox>
                <Box gap={"xl"}>
                  <VBox>
                    <Box className={"bold"}>{t("votes")}</Box>
                    <p>
                      {t("n-for", {
                        n: model.individualVoting.votesFor,
                      })}
                    </p>
                    <p>
                      {t("n-against", {
                        n: model.individualVoting.votesAgainst,
                      })}
                    </p>
                    <p>
                      {t("n-blank", {
                        n: model.individualVoting.votesBlank,
                      })}
                    </p>
                    <p className={"bold"}>
                      {t("n-for", {
                        n: formatPercent(
                          percentOf(
                            model.individualVoting.votesFor,
                            model.numberOfIndividualVotesCast,
                          ),
                        ),
                      })}
                    </p>
                  </VBox>

                  <VBox>
                    <Box className={"bold"}>{t("general-meeting.shareCapital")}</Box>
                    <p>
                      {t("n-for", {
                        n: model.individualVoting.shareCapitalFor,
                      })}
                    </p>
                    <p>
                      {t("n-against", {
                        n: model.individualVoting.shareCapitalAgainst,
                      })}
                    </p>
                    <p>
                      {t("n-blank", {
                        n: model.individualVoting.shareCapitalBlank,
                      })}
                    </p>
                    <p className={"bold"}>
                      {t("n-for", {
                        n: formatPercent(
                          percentOf(
                            model.individualVoting.shareCapitalFor,
                            model.representedShareCapital,
                          ),
                        ),
                      })}
                    </p>
                  </VBox>
                </Box>
              </VBox>
            )}
            <Box>
              <Button
                variant={"secondary"}
                label={t("documents.generalMeetingVoting.registerVotes")}
                onClick={onLinkClick}
                disabled={isDisabled || isElectronicGeneralMeeting}
                tooltip={
                  isElectronicGeneralMeeting
                    ? t("documents.generalMeetingVoting.registerVotes.isElectronicGeneralMeeting")
                    : undefined
                }
              />
            </Box>
          </VBox>
        )}
      </VBox>
    );
  },
);
